<template>
  <div class="content">
    <!-- 保存中は画面全体を白く覆う（一括変更は件数が多いと時間がかかる。契約詳細入力画面と同じ見た目） -->
    <div v-if="saving" class="loading-mask">
      Waiting...
    </div>

    <PageHeader title="賃金">
      <template #title-header-content>
        <!-- 最低賃金マスタ・賃金一括変更の切り替え（登録済み一覧の軸のタブと同じ見た目） -->
        <div class="page-tabs">
          <button
            v-for="t in tabs"
            :key="t.key"
            type="button"
            class="page-tab"
            :class="tab === t.key ? 'active' : ''"
            @click="selectTab(t.key)"
          >
            <Icon :iconName="t.icon" class="w-4 h-4 mr-1" />{{ t.name }}
          </button>
        </div>

        <div v-if="tab === 'bulk'" class="flex-1 flex items-center justify-end mr-2">
          <PrimaryButton text="検索" class="w-32" @click="getTargets()" />
          <div class="ml-4 text-lg font-bold text-white">{{ bulk.rows.length }} 行</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': bulk.loading }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="getTargets()"
          />
        </div>

        <!-- 複数時給の清掃員：検索と人数 -->
        <div v-else-if="tab === 'multi'" class="flex-1 flex items-center justify-end mr-2">
          <PrimaryButton text="検索" class="w-32" @click="getMultiWage()" />
          <div class="ml-4 text-lg font-bold text-white">{{ multi.staffCount }} 名</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': multi.loading }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="getMultiWage()"
          />
        </div>

        <!-- 最低賃金マスタ：他の画面と同じく検索ボタン（マスタを取り直す）と件数 -->
        <div v-else class="flex-1 flex items-center justify-end mr-2">
          <PrimaryButton text="検索" class="w-32" @click="getMinWages()" />
          <div class="ml-4 text-lg font-bold text-white">{{ filteredMinWages.length }} 件</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': minLoading }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="getMinWages()"
          />
        </div>
      </template>

      <template #page-header-content>
        <!-- 最低賃金マスタ：範囲（適用中・予定／全て）と営業所で絞る。「全て」以外は絞っているので黄色 -->
        <div v-if="tab === 'min'" class="flex items-center justify-end gap-4">
          <select v-model="minScope" class="filter-input rounded border-gray-300 text-sm" :class="minScope !== 'all' ? 'filled' : ''" title="範囲">
            <option value="active">適用中・予定</option>
            <option value="all">全て</option>
          </select>
          <select v-model="minFilterOffice" class="filter-input rounded border-gray-300 text-sm" :class="minFilterOffice ? 'filled' : ''" v-tooltip="salesOfficeTip">
            <option value="">全て（{{ salesOfficeCaption }}）</option>
            <option v-for="o in offices" :key="o" :value="o">{{ o }}</option>
          </select>
        </div>

        <!-- 複数時給の清掃員：期間（開始～終了）と検索 -->
        <div v-else-if="tab === 'multi'" class="flex items-center justify-end gap-2">
          <label class="text-sm text-white">期間</label>
          <WmsDateField v-model="multi.startDate" name="multiStartDate" class="w-40" :filled="true" @change="getMultiWage()" />
          <span class="text-white">～</span>
          <WmsDateField v-model="multi.endDate" name="multiEndDate" class="w-40" :filled="true" @change="getMultiWage()" />
          <input
            v-model="multi.name"
            type="search"
            placeholder="物件名・物件カナ・清掃員名・コード 検索"
            class="filter-input w-80 ml-2 rounded border border-gray-300 px-2 text-sm"
            :class="multi.name ? 'filled' : ''"
            @keydown.enter="getMultiWage()"
          />
        </div>

        <!-- 賃金一括変更：適用開始日・営業所・検索（何かで絞っている欄は黄色） -->
        <div v-else class="flex items-center justify-end gap-2">
          <label class="text-sm text-white">適用開始日</label>
          <!-- 日付を選んだときだけ change が来る（読み込み時には来ない）ので、そのまま検索する -->
          <WmsDateField v-model="bulk.startDate" name="startDate" class="w-40" :filled="true" @change="getTargets()" />
          <label class="text-sm text-white ml-2" v-tooltip="salesOfficeTip">{{ salesOfficeCaption }}</label>
          <!-- 区分は東京／千葉／埼玉（埼玉＝最低賃金判定が千葉で物件住所が埼玉県）。「未設定」は移行時に最低賃金判定が決まらなかった雇用指示（行ごとに選ぶ） -->
          <select v-model="bulk.salesOffice" class="filter-input rounded border-gray-300 text-sm filled" v-tooltip="salesOfficeTip" @change="getTargets()">
            <option v-for="o in groupChoices" :key="o" :value="o">{{ o }}</option>
            <option value="none">未設定</option>
          </select>
          <div class="ml-1 mr-3 text-white text-sm whitespace-nowrap">
            最低賃金：<span class="text-lg font-bold">{{ bulk.salesOffice === 'none' ? '行ごとに選ぶ' : yen(bulk.minWages[bulk.salesOffice]) }}</span>
          </div>
          <input
            v-model="bulk.name"
            type="search"
            placeholder="物件名・物件カナ・清掃員名・コード 検索"
            class="filter-input w-80 rounded border border-gray-300 px-2 text-sm"
            :class="bulk.name ? 'filled' : ''"
            @keydown.enter="getTargets()"
          />
        </div>
      </template>
    </PageHeader>

    <main class="absolute top-32 -mt-1 bottom-4 w-screen">
      <div class="h-full px-4 flex flex-col">
        <!-- 最低賃金マスタは枠ごとスクロール。一括変更・複数時給は表だけがスクロールする（見出しと保存の帯を固定するため）ので枠は溢れさせない -->
        <div class="flex-1 min-h-0 rounded-md border border-gray-200 bg-white p-4" :class="tab === 'min' ? 'overflow-auto' : 'overflow-hidden'">
          <!-- 最低賃金マスタ -->
          <AkbsMinWageTable
            v-if="tab === 'min'"
            :rows="filteredMinWages"
            :offices="offices"
            :canEdit="canEdit"
            :saving="saving"
            :loading="minLoading"
            @save="(row, done) => saveMinWage(row).then(done)"
            @delete="deleteMinWage"
          />

          <!-- 複数時給の清掃員（見るだけ） -->
          <AkbsMultiWageTable
            v-else-if="tab === 'multi'"
            :rows="multi.rows"
            :startDate="multi.loadedStartDate"
            :endDate="multi.loadedEndDate"
            :loading="multi.loading"
            :loaded="multi.loaded"
          />

          <!-- 賃金一括変更 -->
          <AkbsWageBulkTable
            v-else
            :rows="bulk.rows"
            :minWages="bulk.minWages"
            :offices="salesOffices"
            :group="bulk.loadedOffice"
            :startDate="bulk.loadedStartDate"
            :isNone="bulk.loadedOffice === 'none'"
            :canEdit="canEdit"
            :saving="saving"
            :loading="bulk.loading"
            :loaded="bulk.loaded"
            @save="saveWages"
          />
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PageHeader from '@components/PageHeader.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import WmsDateField from '@wmscomponents/WmsDateField.vue'
import AkbsMinWageTable from '../components/AkbsMinWageTable.vue'
import AkbsWageBulkTable from '../components/AkbsWageBulkTable.vue'
import AkbsMultiWageTable from '../components/AkbsMultiWageTable.vue'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as utils from '@libs/utils'
import * as moment from 'moment'
import { SALES_OFFICE_CAPTION, SALES_OFFICE_TIP, SALES_OFFICE_SELECTIONS, MIN_WAGE_OFFICE_SELECTIONS } from '@libs/constantsAKBS'
import PageModel from '../../main/pages/PageModel.vue'

// 画面のタブ
const TABS = [
  { key: 'min', name: '最低賃金マスタ', icon: 'CurrencyYen' },
  { key: 'bulk', name: '賃金一括変更', icon: 'ClipboardList' },
  { key: 'multi', name: '複数時給の清掃員', icon: 'Search' }
]

export default {
  extends: PageModel,

  components: {
    Icon,
    PageHeader,
    PrimaryButton,
    WmsDateField,
    AkbsMinWageTable,
    AkbsWageBulkTable,
    AkbsMultiWageTable
  },

  data() {
    return {
      tabs: TABS,
      tab: 'min',
      // 書き込みできるか（兼任を含めて人事総務経理に所属しているか、管理者権限がある）。見るのは誰でも可
      canEdit: false,
      saving: false,

      // 最低賃金判定（東京／千葉）の呼び方と説明
      salesOfficeCaption: SALES_OFFICE_CAPTION,
      salesOfficeTip: SALES_OFFICE_TIP,
      // 雇用指示の最低賃金判定の候補（東京／千葉。未設定の行で選ぶ）
      salesOffices: SALES_OFFICE_SELECTIONS,
      // 一括変更の区分（東京／千葉／埼玉）
      groupChoices: MIN_WAGE_OFFICE_SELECTIONS,

      // 最低賃金マスタ
      minWages: [],
      minLoading: false,
      // 範囲（active：今日以降に効く行＝適用中・予定、all：終了した行も）。初期値は適用中・予定
      minScope: 'active',
      minFilterOffice: '',
      // マスタにある区分（東京 → 千葉 → 埼玉）
      offices: [],

      // 賃金一括変更
      bulk: {
        startDate: '',
        salesOffice: '',
        name: '',
        // 検索したときの条件（表はこの条件で出している）
        loadedStartDate: '',
        loadedOffice: '',
        loaded: false,
        loading: false,
        minWages: {},
        rows: []
      },

      // 複数時給の清掃員（期間の中の同じ日に、違う雇用指示で違う時給が効いている人）
      multi: {
        startDate: '',
        endDate: '',
        name: '',
        // 検索したときの期間（表はこの期間で出している）
        loadedStartDate: '',
        loadedEndDate: '',
        loaded: false,
        loading: false,
        rows: [],
        staffCount: 0
      }
    }
  },

  computed: {
    /**
     * 範囲と営業所で絞った最低賃金マスタ
     */
    filteredMinWages() {
      return this.minWages.filter(m => {
        if (this.minScope === 'active' && !m.isCurrent && !m.isFuture) {
          return false
        }
        return !this.minFilterOffice || m.salesOffice === this.minFilterOffice
      })
    }
  },

  async created() {
    // 書き込みできるのは、兼任を含めて人事総務経理に所属している人か、管理者権限がある人（ユーザー決定 2026-10-09。雇用指示の AKBS 権限は見ない）
    const auth = this.$store.getters.auth
    const dep = this.$store.getters.dualPosition || []
    this.canEdit = utils.manager(dep) || !!(auth && auth.adminAuth)

    await this.getMinWages()

    // 適用開始日の初期値は、マスタで一番近い未来の開始日。無ければ今日
    const future = this.minWages.filter(m => m.isFuture).map(m => m.startDate).sort()
    this.bulk.startDate = future[0] || this.today()
    this.bulk.salesOffice = this.groupChoices[0]

    // 複数時給の期間の初期値は今月（1日～月末）
    this.multi.startDate = moment().startOf('month').format('YYYY-MM-DD')
    this.multi.endDate = moment().endOf('month').format('YYYY-MM-DD')
  },

  methods: {
    /**
     * タブを切り替えて、そのタブの今の条件で検索する（検索ボタンを押す手間を省く。同じタブをもう一度押しても取り直す）
     * @param {String} key タブ（min／bulk／multi）
     */
    selectTab(key) {
      this.tab = key
      if (key === 'min') {
        this.getMinWages()
      } else if (key === 'bulk') {
        this.getTargets()
      } else if (key === 'multi') {
        this.getMultiWage()
      }
    },

    /**
     * マスタにある区分（重複なし。東京 → 千葉 → 埼玉、それ以外は文字順で後ろ）
     * @param {Array} rows 最低賃金マスタの行
     */
    officesOf(rows) {
      const order = o => {
        const i = this.groupChoices.indexOf(o)
        return i < 0 ? this.groupChoices.length : i
      }
      return [...new Set(rows.map(m => m.salesOffice).filter(o => o))]
        .sort((a, b) => order(a) - order(b) || a.localeCompare(b, 'ja'))
    },

    /**
     * 最低賃金マスタと営業所の候補を取り直す
     */
    async getMinWages() {
      this.minLoading = true
      try {
        const res = await backend.searchData('akbsWage/getMinWageList', {})
        this.minWages = res.data && Array.isArray(res.data.data) ? res.data.data : []
        this.offices = this.officesOf(this.minWages)
      } catch (error) {
        console.log(error)
        this.minWages = []
      } finally {
        this.minLoading = false
      }
    },

    /**
     * 最低賃金マスタを1件保存（追加・修正）
     * @param {Object} row { id, salesOffice, startDate, minWage }
     * @returns 保存できたか（子の表はこれを見て入力欄を空にする）
     */
    async saveMinWage(row) {
      this.saving = true
      try {
        const res = await backend.postData('akbsWage/saveMinWage', { ...row, updateUser: this.$store.getters.user.id })
        if (await this.failed(res)) {
          return false
        }
        this.minWages = res.data.data
        this.offices = this.officesOf(this.minWages)
        return true
      } catch (error) {
        console.log(error)
        return false
      } finally {
        this.saving = false
      }
    },

    /**
     * 最低賃金マスタを1件削除
     * @param {Object} row 行
     */
    async deleteMinWage(row) {
      const answer = await dialogs.showWarningConfirmDialog('最低賃金の削除', `${row.salesOffice} ${this.slash(row.startDate)}～ の ${this.yen(row.minWage)} を削除します。よろしいですか？`, '削除する')
      if (answer !== 'YES') {
        return
      }
      this.saving = true
      try {
        const res = await backend.postData('akbsWage/deleteMinWage', { id: row.id, updateUser: this.$store.getters.user.id })
        if (await this.failed(res)) {
          return
        }
        this.minWages = res.data.data
        this.offices = this.officesOf(this.minWages)
      } catch (error) {
        console.log(error)
      } finally {
        this.saving = false
      }
    },

    /**
     * 一括変更の対象を検索する
     */
    async getTargets() {
      const b = this.bulk
      if (!b.startDate) {
        await dialogs.showErrorDialog('適用開始日', '適用開始日を入れてください。')
        return
      }
      b.loading = true
      // 前回の結果は先に消す（取れなかったときに古い結果や条件の表示が残らないように）
      b.rows = []
      b.minWages = {}
      b.loaded = false
      try {
        const res = await backend.searchData('akbsWage/getWageTargets', { startDate: b.startDate, salesOffice: b.salesOffice, name: b.name })
        const data = res.data && res.data.data ? res.data.data : { minWages: {}, rows: [] }
        b.minWages = data.minWages || {}
        b.rows = Array.isArray(data.rows) ? data.rows : []
        b.loadedStartDate = b.startDate
        b.loadedOffice = b.salesOffice
        b.loaded = true
      } catch (error) {
        console.log(error)
      } finally {
        b.loading = false
      }
    },

    /**
     * 賃金を一括で変更する
     * @param {Array} items 変更する行（wageId, salesOffice, newWage, nameStaff）
     */
    async saveWages(items) {
      const b = this.bulk
      const officeText = b.loadedOffice === 'none' ? '' : `${b.loadedOffice}の`
      const answer = await dialogs.showConfirmDialog(
        '賃金の一括変更',
        `${officeText}${items.length}件の賃金を本番のAKBSに書き込みます。よろしいですか？\r\n・${this.slash(b.loadedStartDate)} から新しい時給で適用されます`,
        '変更する'
      )
      if (answer !== 'YES') {
        return
      }

      this.saving = true
      try {
        const res = await backend.postData('akbsWage/saveWages', {
          startDate: b.loadedStartDate,
          updateUser: this.$store.getters.user.id,
          rows: items.map(i => ({ wageId: i.wageId, salesOffice: i.salesOffice, newWage: i.newWage, nameStaff: i.nameStaff }))
        })
        if (await this.failed(res)) {
          return
        }
        // 同じ開始日の賃金が既にあって同じか高い額だった行は何もしていないので、件数を添える
        const { count, skipped } = res.data.data
        let message = `${count}件の賃金を変更しました。`
        if (skipped) {
          message += `\r\n${skipped}件は ${this.slash(b.loadedStartDate)} からの賃金が既にあり、同じか高い時給だったので変更していません。`
        }
        await dialogs.showSuccessDialog('賃金の一括変更', message)
        await this.getTargets()
      } catch (error) {
        console.log(error)
      } finally {
        this.saving = false
      }
    },

    /**
     * 複数時給の清掃員を検索する（期間の中の同じ日に、違う雇用指示で違う時給が効いている人）
     */
    async getMultiWage() {
      const m = this.multi
      if (!m.startDate || !m.endDate) {
        await dialogs.showErrorDialog('期間', '期間の開始と終了を入れてください。')
        return
      }
      if (m.startDate > m.endDate) {
        await dialogs.showErrorDialog('期間', '期間の終了は開始より後の日にしてください。')
        return
      }
      m.loading = true
      // 前回の結果は先に消す（取れなかったときに古い結果や条件の表示が残らないように）
      m.rows = []
      m.staffCount = 0
      m.loaded = false
      try {
        const res = await backend.searchData('akbsWage/getMultiWageStaff', { startDate: m.startDate, endDate: m.endDate, name: m.name })
        m.rows = res.data && Array.isArray(res.data.data) ? res.data.data : []
        m.staffCount = new Set(m.rows.map(r => r.staffDailyCleaning)).size
        m.loadedStartDate = m.startDate
        m.loadedEndDate = m.endDate
        m.loaded = true
      } catch (error) {
        console.log(error)
      } finally {
        m.loading = false
      }
    },

    /**
     * バックが画面向けのエラーを返したら出す
     * @param {*} res レスポンス
     * @returns エラーだったか
     */
    async failed(res) {
      const error = res && res.data ? res.data.error : null
      if (error) {
        await dialogs.showErrorDialog('保存できません', error)
        return true
      }
      if (!res || !res.data || res.data.data === null || res.data.data === undefined) {
        await dialogs.showErrorDialog('保存できません', '保存に失敗しました。もう一度お試しください。')
        return true
      }
      return false
    },

    /**
     * 今日（yyyy-mm-dd）
     */
    today() {
      return moment().format('YYYY-MM-DD')
    },

    /**
     * yyyy-mm-dd → yyyy/mm/dd
     */
    slash(day) {
      return day ? moment(day).format('YYYY/MM/DD') : ''
    },

    /**
     * 金額（1,250円。無ければ -）
     */
    yen(val) {
      if (val === null || val === undefined || val === '') {
        return '-'
      }
      return `${Number(val).toLocaleString()}円`
    }
  }
}
</script>

<style scoped>
/* 保存中のマスク（契約詳細入力画面と同じ） */
.loading-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  color: #6b7280;
  font-weight: 700;
  font-size: 14px;
}

/* 絞り込みの入力欄は高さを揃える */
.filter-input {
  height: 38px;
  box-sizing: border-box;
}

/* 何かで絞っている欄（申請一覧と同じ色） */
.filter-input.filled {
  font-weight: bold;
  background-color: #efdf8a;
}

/* 画面のタブ（登録済み一覧の軸のタブと同じ。ヘッダーの真ん中に白い枠） */
.page-tabs {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  gap: 6px;
  padding: 5px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.page-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 150px;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  background: #9ca3af;
  color: #fff;
  font-size: 0.875rem;
  font-weight: bold;
  white-space: nowrap;
  cursor: pointer;
}

.page-tab:hover {
  background: #6b7280;
}

.page-tab.active {
  background: #1d4ed8;
}

.page-tab.active:hover {
  background: #1e40af;
}
</style>
